import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { mediaAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Plus, 
  Image as ImageIcon, 
  Trash2, 
  Upload,
  Filter,
  Grid,
  List,
  X,
  Tag,
  Search,
  ShoppingBag,
  Tent,
  Building2,
  Camera,
  Cloud
} from 'lucide-react';
import { HelpButton } from '../components/HelpButton';
import { CloudImportDialog } from '../components/media/CloudImportDialog';
import api from '../lib/api';

const MEDIA_TYPES = [
  { value: 'product', label: 'Product Photos', icon: ShoppingBag },
  { value: 'booth', label: 'Booth Setup', icon: Tent },
  { value: 'logo', label: 'Logos', icon: Building2 },
  { value: 'gallery', label: 'Event Gallery', icon: Camera },
  { value: 'other', label: 'Other', icon: ImageIcon },
];

export const MediaLibrary = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedImage, setSelectedImage] = useState(null);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isCloudImportOpen, setIsCloudImportOpen] = useState(false);
  
  const [uploadData, setUploadData] = useState({
    file: null,
    preview: null,
    media_type: 'product',
    tags: '',
    description: '',
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchMedia();
  }, [user, navigate, typeFilter]);

  const fetchMedia = async () => {
    try {
      setLoading(true);
      const params = {};
      if (typeFilter !== 'all') params.media_type = typeFilter;
      const response = await mediaAPI.getAll(params);
      setMedia(response.data);
    } catch (error) {
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    
    const preview = URL.createObjectURL(file);
    setUploadData({ ...uploadData, file, preview });
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadData.file) {
      toast.error('Please select a file');
      return;
    }
    
    try {
      setUploading(true);
      await mediaAPI.upload(
        uploadData.file,
        uploadData.media_type,
        uploadData.tags,
        uploadData.description
      );
      toast.success('Image uploaded');
      setIsUploadOpen(false);
      setUploadData({ file: null, preview: null, media_type: 'product', tags: '', description: '' });
      fetchMedia();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this image?')) return;
    
    try {
      await mediaAPI.delete(id);
      toast.success('Image deleted');
      setSelectedImage(null);
      fetchMedia();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleCloudImport = async (files, source) => {
    try {
      const endpoint = source === 'google_drive' 
        ? '/cloud-storage/import/google-drive'
        : '/cloud-storage/import/dropbox';
      
      const response = await api.post(endpoint, {
        files: files,
        source: source
      });
      
      const result = response.data;
      
      if (result.imported_count > 0) {
        toast.success(`Imported ${result.imported_count} image(s) from ${source === 'google_drive' ? 'Google Drive' : 'Dropbox'}`);
        setIsCloudImportOpen(false);
        fetchMedia();
      }
      
      if (result.failed_count > 0) {
        toast.error(`Failed to import ${result.failed_count} file(s)`);
      }
    } catch (error) {
      console.error('Cloud import error:', error);
      toast.error(error.response?.data?.detail || 'Failed to import files');
      throw error;
    }
  };

  // Utility: the backend returns `tags` as a comma-separated string, but older
  // records (and some code paths) still return arrays. Normalise to an array
  // of trimmed strings for consistent rendering/filtering.
  const asTagArray = (t) => {
    if (!t) return [];
    if (Array.isArray(t)) return t.filter(Boolean);
    return String(t).split(',').map((x) => x.trim()).filter(Boolean);
  };

  const filteredMedia = media.filter(item => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesFilename = item.filename?.toLowerCase().includes(query);
      const matchesDescription = item.description?.toLowerCase().includes(query);
      const matchesTags = asTagArray(item.tags).some(t => t.toLowerCase().includes(query));
      return matchesFilename || matchesDescription || matchesTags;
    }
    return true;
  });

  const getMediaTypeIcon = (type) => {
    const found = MEDIA_TYPES.find(t => t.value === type);
    return found?.icon || ImageIcon;
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#FFFFFF] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div className="flex items-center gap-2">
            <div>
              <h1 className="text-3xl font-bold text-[#1E3A5F] font-['Outfit']">Media Library</h1>
              <p className="text-gray-600 mt-1">Manage product photos, booth images, and logos</p>
            </div>
            <HelpButton tutorialId="media" />
          </div>
          <div className="flex items-center gap-2">
            <Button 
              onClick={() => setIsCloudImportOpen(true)}
              variant="outline"
              className="border-2 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F]/5"
              data-testid="cloud-import-btn"
            >
              <Cloud className="w-4 h-4 mr-2" />
              Import from Cloud
            </Button>
            <Button 
              onClick={() => setIsUploadOpen(true)} 
              className="btn-primary"
              data-testid="upload-media-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Image
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-4 mb-6 shadow-hard">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by name or tag..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-media"
              />
            </div>
            
            {/* Type Filter */}
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="type-filter">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {MEDIA_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* View Toggle */}
            <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white shadow' : ''}`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-white shadow' : ''}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Media Grid/List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1E3A5F]"></div>
          </div>
        ) : filteredMedia.length === 0 ? (
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-12 text-center shadow-hard">
            <ImageIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-[#1E3A5F] mb-2">No images found</h3>
            <p className="text-gray-600 mb-4">
              {searchQuery || typeFilter !== 'all' 
                ? "Try adjusting your filters" 
                : "Upload your first image to get started"
              }
            </p>
            {!searchQuery && typeFilter === 'all' && (
              <Button onClick={() => setIsUploadOpen(true)} className="btn-primary">
                <Upload className="w-4 h-4 mr-2" />
                Upload Image
              </Button>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {filteredMedia.map((item) => {
              const TypeIcon = getMediaTypeIcon(item.media_type);
              return (
                <div
                  key={item.id}
                  className="group relative aspect-square bg-white rounded-xl border-2 border-[#1E3A5F] overflow-hidden shadow-hard hover:shadow-hard-hover transition-all cursor-pointer"
                  onClick={() => setSelectedImage(item)}
                  data-testid={`media-item-${item.id}`}
                >
                  <img
                    src={(item.file_url || item.url)?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${item.file_url || item.url}` : (item.file_url || item.url)}
                    alt={item.filename}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <p className="text-white text-sm font-medium truncate">{item.filename}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <TypeIcon className="w-3 h-3 text-white/80" />
                        <span className="text-white/80 text-xs capitalize">{item.media_type}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] overflow-hidden shadow-hard divide-y divide-gray-100">
            {filteredMedia.map((item) => {
              const TypeIcon = getMediaTypeIcon(item.media_type);
              return (
                <div
                  key={item.id}
                  className="flex items-center gap-4 p-4 hover:bg-[#FFEDDF]/50 transition-colors cursor-pointer"
                  onClick={() => setSelectedImage(item)}
                >
                  <div className="w-16 h-16 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0">
                    <img
                      src={(item.file_url || item.url)?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${item.file_url || item.url}` : (item.file_url || item.url)}
                      alt={item.filename}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#1E3A5F] truncate">{item.filename}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1 capitalize">
                        <TypeIcon className="w-3 h-3" />
                        {item.media_type}
                      </span>
                      {asTagArray(item.tags).length > 0 && (
                        <span className="flex items-center gap-1">
                          <Tag className="w-3 h-3" />
                          {asTagArray(item.tags).join(', ')}
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(item.id);
                    }}
                    className="text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {/* Stats */}
        {media.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-6">
            {MEDIA_TYPES.map((type) => {
              const count = media.filter(m => m.media_type === type.value).length;
              const TypeIcon = type.icon;
              return (
                <div 
                  key={type.value}
                  className="bg-white rounded-xl border-2 border-[#1E3A5F] p-4 shadow-hard text-center"
                >
                  <TypeIcon className="w-6 h-6 mx-auto text-[#1E3A5F] mb-2" />
                  <p className="text-2xl font-bold text-[#1E3A5F]">{count}</p>
                  <p className="text-xs text-gray-500">{type.label}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Upload Dialog */}
      <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1E3A5F]">Upload Image</DialogTitle>
            <DialogDescription>
              Add an image to your media library
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleUpload} className="space-y-4">
            {/* File Input */}
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              {uploadData.preview ? (
                <div className="relative">
                  <img
                    src={uploadData.preview}
                    alt="Preview"
                    className="w-full h-48 object-cover rounded-lg border-2 border-[#1E3A5F]"
                  />
                  <button
                    type="button"
                    onClick={() => setUploadData({...uploadData, file: null, preview: null})}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full h-48 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center hover:border-[#1E3A5F] transition-colors"
                >
                  <Upload className="w-8 h-8 text-gray-400 mb-2" />
                  <span className="text-gray-600">Click to select image</span>
                  <span className="text-xs text-gray-400 mt-1">Max 5MB</span>
                </button>
              )}
            </div>
            
            <div>
              <Label>Image Type</Label>
              <Select
                value={uploadData.media_type}
                onValueChange={(value) => setUploadData({...uploadData, media_type: value})}
              >
                <SelectTrigger data-testid="upload-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MEDIA_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Tags (comma separated)</Label>
              <Input
                value={uploadData.tags}
                onChange={(e) => setUploadData({...uploadData, tags: e.target.value})}
                placeholder="e.g., jewelry, necklace, silver"
                data-testid="upload-tags-input"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Input
                value={uploadData.description}
                onChange={(e) => setUploadData({...uploadData, description: e.target.value})}
                placeholder="Brief description..."
                data-testid="upload-description-input"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsUploadOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="btn-primary" 
                disabled={!uploadData.file || uploading}
                data-testid="confirm-upload-btn"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="sm:max-w-2xl">
          {selectedImage && (
            <>
              <div className="relative">
                <img
                  src={(selectedImage.file_url || selectedImage.url)?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${selectedImage.file_url || selectedImage.url}` : (selectedImage.file_url || selectedImage.url)}
                  alt={selectedImage.filename}
                  className="w-full max-h-[60vh] object-contain rounded-lg"
                />
              </div>
              <div className="mt-4">
                <h3 className="font-semibold text-[#1E3A5F]">{selectedImage.filename}</h3>
                <div className="flex items-center gap-4 text-sm text-gray-500 mt-2">
                  <span className="capitalize">{selectedImage.media_type}</span>
                  {asTagArray(selectedImage.tags).length > 0 && (
                    <span>Tags: {asTagArray(selectedImage.tags).join(', ')}</span>
                  )}
                </div>
                {selectedImage.description && (
                  <p className="text-gray-600 mt-2">{selectedImage.description}</p>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => handleDelete(selectedImage.id)}
                  className="text-red-600 border-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
                <Button variant="outline" onClick={() => setSelectedImage(null)}>
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Cloud Import Dialog */}
      <CloudImportDialog
        open={isCloudImportOpen}
        onOpenChange={setIsCloudImportOpen}
        onImport={handleCloudImport}
      />
    </div>
  );
};

export default MediaLibrary;
